import React, { useState, useMemo } from 'react';
import { Package, Table2, Armchair, Shirt, UtensilsCrossed, Sparkles, Users, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';

const RentalsSection = ({ 
  rentals, 
  selectedRentals, 
  onToggleRental, 
  onUpdateQuantity, 
  onUpdateColor,
  isExpanded, 
  onToggle,
  calculateTotal 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeMainCategory, setActiveMainCategory] = useState('all');

  // Category configuration
  const categoryConfig = {
    tables: { label: 'Tables', icon: Table2, mainGroup: 'furniture' },
    chairs: { label: 'Chairs', icon: Armchair, mainGroup: 'furniture' },
    linens: { label: 'Linens', icon: Shirt, mainGroup: 'linens' },
    china: { label: 'China', icon: UtensilsCrossed, mainGroup: 'tableware' },
    flatware: { label: 'Flatware', icon: UtensilsCrossed, mainGroup: 'tableware' },
    glassware: { label: 'Glassware', icon: UtensilsCrossed, mainGroup: 'tableware' },
    serving: { label: 'Serving', icon: Package, mainGroup: 'service' },
    decor: { label: 'Decor', icon: Sparkles, mainGroup: 'decor' },
    centerpieces: { label: 'Centerpieces', icon: Sparkles, mainGroup: 'decor' },
    services: { label: 'Services', icon: Users, mainGroup: 'service' },
    venue: { label: 'Venue', icon: Package, mainGroup: 'venue' },
    chair_covers: { label: 'Chair Covers', icon: Armchair, mainGroup: 'linens' },
    bar_addon: { label: 'Bar Add-ons', icon: UtensilsCrossed, mainGroup: 'bar' },
    bar_service: { label: 'Bar Service', icon: UtensilsCrossed, mainGroup: 'bar' }
  };

  const mainGroups = {
    furniture: { label: 'Tables & Chairs', categories: ['tables', 'chairs'] },
    linens: { label: 'Linens & Covers', categories: ['linens', 'chair_covers'] },
    tableware: { label: 'Tableware', categories: ['china', 'flatware', 'glassware'] },
    service: { label: 'Service & Equipment', categories: ['serving', 'services'] },
    decor: { label: 'Decor', categories: ['decor', 'centerpieces'] },
    venue: { label: 'Venue', categories: ['venue'] },
    bar: { label: 'Bar', categories: ['bar_addon', 'bar_service'] }
  };

  // Filter and group rentals
  const filteredRentals = useMemo(() => {
    let filtered = rentals;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.name?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }
    
    if (activeMainCategory !== 'all') {
      const allowedCategories = mainGroups[activeMainCategory]?.categories || [];
      filtered = filtered.filter(r => allowedCategories.includes(r.category));
    }
    
    return filtered;
  }, [rentals, searchQuery, activeMainCategory]);

  // Group by category then sub_category
  const groupedRentals = useMemo(() => {
    const groups = {};
    filteredRentals.forEach(rental => {
      const cat = rental.category || 'other';
      if (!groups[cat]) groups[cat] = {};
      const subCat = rental.sub_category || 'general';
      if (!groups[cat][subCat]) groups[cat][subCat] = [];
      groups[cat][subCat].push(rental);
    });
    return groups;
  }, [filteredRentals]);

  const isSelected = (rentalId) => selectedRentals.find(r => r.id === rentalId);

  const toggleCategoryExpand = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getPrice = (rental) => rental.price_per_unit || rental.price || rental.item_price || 0;

  const getCategoryIcon = (category) => {
    const Icon = categoryConfig[category]?.icon || Package;
    return <Icon size={14} />;
  };

  // Count items per main group
  const mainGroupCounts = useMemo(() => {
    const counts = { all: rentals.length };
    Object.entries(mainGroups).forEach(([key, group]) => {
      counts[key] = rentals.filter(r => group.categories.includes(r.category)).length;
    });
    return counts;
  }, [rentals]);

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
        data-testid="rentals-section-toggle"
      >
        <span className="flex items-center gap-2">
          <Package size={18} />
          Rentals & Equipment ({selectedRentals.length} selected)
          {calculateTotal() > 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              (${calculateTotal().toFixed(2)})
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>
      
      {isExpanded && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search rentals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm bg-white"
              data-testid="rental-search"
            />
          </div>

          {/* Main category tabs */}
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setActiveMainCategory('all')}
              className={`px-2 py-1 text-xs rounded ${activeMainCategory === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              All ({mainGroupCounts.all})
            </button>
            {Object.entries(mainGroups).map(([key, group]) => (
              mainGroupCounts[key] > 0 && (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveMainCategory(key)}
                  className={`px-2 py-1 text-xs rounded ${activeMainCategory === key ? 'bg-[#1e3a5f] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  {group.label} ({mainGroupCounts[key]})
                </button>
              )
            ))}
          </div>

          {/* Selected items summary */}
          {selectedRentals.length > 0 && (
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <p className="text-xs font-medium text-[#1e3a5f] mb-1">Selected ({selectedRentals.length}):</p>
              <div className="flex flex-wrap gap-1">
                {selectedRentals.slice(0, 8).map(item => (
                  <span key={item.id} className="text-xs bg-white px-2 py-0.5 rounded">
                    {item.name} x{item.quantity}
                  </span>
                ))}
                {selectedRentals.length > 8 && (
                  <span className="text-xs text-gray-500">+{selectedRentals.length - 8} more</span>
                )}
              </div>
            </div>
          )}

          {/* Rentals list */}
          <div className="max-h-80 overflow-y-auto space-y-3">
            {Object.entries(groupedRentals).map(([category, subCategories]) => (
              <div key={category} className="bg-white rounded-lg border">
                <button
                  type="button"
                  onClick={() => toggleCategoryExpand(category)}
                  className="w-full flex items-center justify-between p-2 hover:bg-gray-50"
                >
                  <span className="flex items-center gap-2 font-medium text-sm text-[#1e3a5f]">
                    {getCategoryIcon(category)}
                    <span className="capitalize">{categoryConfig[category]?.label || category.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-400">
                      ({Object.values(subCategories).flat().length})
                    </span>
                  </span>
                  {expandedCategories[category] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                
                {expandedCategories[category] && (
                  <div className="p-2 pt-0 space-y-2">
                    {Object.entries(subCategories).map(([subCat, items]) => (
                      <div key={subCat}>
                        {subCat !== 'general' && (
                          <p className="text-xs font-medium text-gray-500 capitalize mb-1 ml-1">
                            {subCat.replace(/_/g, ' ')}
                          </p>
                        )}
                        <div className="space-y-1">
                          {items.map((rental) => {
                            const selected = isSelected(rental.id);
                            const price = getPrice(rental);
                            return (
                              <div
                                key={rental.id}
                                className={`p-2 rounded border transition-all ${
                                  selected ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : 'border-gray-100 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div 
                                    className="flex-1 cursor-pointer"
                                    onClick={() => onToggleRental(rental)}
                                  >
                                    <p className="text-sm font-medium">{rental.name}</p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <span className="font-semibold text-[#1e3a5f]">${price.toFixed(2)}</span>
                                      <span>/{rental.unit || 'each'}</span>
                                      {rental.color && <span className="bg-gray-100 px-1 rounded">{rental.color}</span>}
                                      {rental.dimensions && <span>{rental.dimensions}</span>}
                                    </div>
                                  </div>
                                  {selected && (
                                    <div className="flex items-center gap-2">
                                      {rental.color_options?.length > 0 && (
                                        <Select
                                          value={selected.color || ''}
                                          onValueChange={(value) => onUpdateColor(rental.id, value)}
                                        >
                                          <SelectTrigger className="w-20 h-7 text-xs">
                                            <SelectValue placeholder="Color" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {rental.color_options.map((color) => (
                                              <SelectItem key={color} value={color}>{color}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                      <div className="flex items-center border rounded bg-white">
                                        <button
                                          type="button"
                                          onClick={() => onUpdateQuantity(rental.id, Math.max(0, selected.quantity - 1))}
                                          className="px-2 py-0.5 text-gray-600 hover:bg-gray-100 text-sm"
                                        >−</button>
                                        <span className="px-2 text-sm min-w-[24px] text-center">{selected.quantity}</span>
                                        <button
                                          type="button"
                                          onClick={() => onUpdateQuantity(rental.id, selected.quantity + 1)}
                                          className="px-2 py-0.5 text-gray-600 hover:bg-gray-100 text-sm"
                                        >+</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {Object.keys(groupedRentals).length === 0 && (
              <p className="text-gray-500 text-sm text-center py-6">
                {searchQuery ? 'No items match your search' : 'No rental items available'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalsSection;
